import React, { useState } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { patientAPI } from '../../../utils/api';

const PatientSupport = () => {
  const { showSuccess, showError } = useNotification();
  const [ticket, setTicket] = useState({
    subject: '',
    description: '',
    priority: 'medium'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!ticket.subject || !ticket.description) {
      showError('Please fill all required fields');
      return;
    }

    try {
      const res = await patientAPI.submitSupportTicket(ticket);
      if (res.data?.success) {
        showSuccess('Support ticket submitted successfully');
        setTicket({ subject: '', description: '', priority: 'medium' });
      } else {
        showError(res.data?.message || 'Failed to submit ticket');
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to submit support ticket');
    }
  };

  const faqs = [
    {
      question: "How do I book a session?",
      answer: "Go to the Specialists section, select a provider, choose session type, and click Book Now."
    },
    {
      question: "What are the session charges?",
      answer: "All sessions cost ₹499 as fixed by admin."
    },
    {
      question: "How do subscriptions work?",
      answer: "Subscribe to get session limits. Admin sets your monthly session allowance."
    },
    {
      question: "Who approves specialists?",
      answer: "All specialists must be approved by admin before appearing to users."
    }
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gold">Support Center</h1>

      {/* Quick Help Cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-800 rounded-xl p-4 text-center">
          <div className="w-12 h-12 bg-gold rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </div>
          <h3 className="font-semibold">Call Support</h3>
          <p className="text-gray-400 text-sm">1800-XXX-XXXX</p>
        </div>

        <div className="bg-gray-800 rounded-xl p-4 text-center">
          <div className="w-12 h-12 bg-gold rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="font-semibold">Email Support</h3>
          <p className="text-gray-400 text-sm">support@vivara.com</p>
        </div>
      </div>

      {/* Submit Ticket Form */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-4">Submit Support Ticket</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-400 mb-2">Subject *</label>
            <input
              type="text"
              value={ticket.subject}
              onChange={(e) => setTicket({ ...ticket, subject: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg py-3 px-4 text-light placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold"
              placeholder="Brief description of your issue"
            />
          </div>

          <div>
            <label className="block text-gray-400 mb-2">Priority</label>
            <select
              value={ticket.priority}
              onChange={(e) => setTicket({ ...ticket, priority: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg py-3 px-4 text-light focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-400 mb-2">Description *</label>
            <textarea
              value={ticket.description}
              onChange={(e) => setTicket({ ...ticket, description: e.target.value })}
              rows="4"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg py-3 px-4 text-light placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold"
              placeholder="Detailed description of your issue"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-gold text-dark py-3 rounded-lg font-medium"
          >
            Submit Ticket
          </button>
        </form>
      </div>

      {/* FAQ Section */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-4">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="border-b border-gray-700 pb-4 last:border-0 last:pb-0">
              <h3 className="font-medium text-lg mb-2">{faq.question}</h3>
              <p className="text-gray-400">{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PatientSupport;
